import React from 'react';
import { Button, Tag, Typography } from 'antd';
import {
  CheckOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import MarkdownBody from './MarkdownBody';
import TrustPill from './TrustPill';
import CitationList from './CitationList';
import FeedbackButtons from './FeedbackButtons';
import ScreeningResult from './ScreeningResult';
import NextActions from './NextActions';
import SourceLimitation from './SourceLimitation';
import CoverageDisclaimer from './CoverageDisclaimer';
import PhiBlockedNotice from './PhiBlockedNotice';
import SubAnswerList from './SubAnswerList';
import { formatRelativeTime } from '../utils/time';
import { screeningStatusColor } from '../utils/formatters';

const { Text } = Typography;

function MessageBubble({ message, isLatest, onFeedback, onOpenScreener, onCopyAnswer, onPrintMessage }) {
  const isUser = message.sender === 'user';
  const isPhiBlock = !isUser && message.error === 'phi_not_allowed';
  const hasStructuredAnswers = !isUser && Array.isArray(message.sub_answers) && message.sub_answers.length > 1;
  const singleSubAnswer =
    !isUser && Array.isArray(message.sub_answers) && message.sub_answers.length === 1 ? message.sub_answers[0] : null;

  return (
    <div
      data-message-id={message.id}
      className={`glp1-message glp1-message--${isUser ? 'user' : 'assistant'}${message.print_target ? ' glp1-print-target' : ''}`}
    >
      <div className="glp1-message-bubble">
        <div className="glp1-message-header">
          {isUser ? <UserOutlined aria-hidden="true" /> : <RobotOutlined aria-hidden="true" />}
          <Text strong style={{ color: isUser ? '#ffffff' : '#344054' }}>
            {isUser ? 'You' : 'MedHug Bridge Assistant'}
          </Text>
          {isUser && message.privacy_blocked ? <Tag color="red">Blocked</Tag> : null}
          {isPhiBlock ? <Tag color="red">PHI not accepted</Tag> : null}
          {!isUser && !isPhiBlock ? <TrustPill message={message} /> : null}
          {singleSubAnswer?.answerable === false ? <Tag color="red">Source limited</Tag> : null}
          {!isUser && message.screening ? <Tag color={screeningStatusColor(message.screening.status)}>Screener</Tag> : null}
          {message.created_at ? (
            <span className="glp1-message-time" title={new Date(message.created_at).toLocaleString()}>
              {formatRelativeTime(message.created_at)}
            </span>
          ) : null}
          {!isUser && !isPhiBlock && (message.direct_answer || message.body_markdown) ? (
            <button
              type="button"
              className="glp1-message-action"
              aria-label="Copy answer to clipboard"
              title="Copy answer"
              onClick={() => onCopyAnswer(message)}
            >
              {message.copy_state === 'copied' ? <CheckOutlined /> : <CopyOutlined />}
            </button>
          ) : null}
          {!isUser && !isPhiBlock && (message.direct_answer || message.body_markdown) ? (
            <button
              type="button"
              className="glp1-message-action"
              aria-label="Print or save as PDF"
              title="Print / Save PDF"
              onClick={() => onPrintMessage(message)}
            >
              <PrinterOutlined />
            </button>
          ) : null}
        </div>

        {isPhiBlock ? (
          <PhiBlockedNotice />
        ) : hasStructuredAnswers ? (
          <SubAnswerList subAnswers={message.sub_answers} />
        ) : isUser ? (
          <p className="glp1-answer-text">{message.answer}</p>
        ) : (
          <>
            {message.direct_answer ? (
              <p className="glp1-direct-answer">{message.direct_answer}</p>
            ) : null}
            {message.body_markdown ? (
              <MarkdownBody>{message.body_markdown}</MarkdownBody>
            ) : !message.direct_answer ? (
              <p className="glp1-answer-text">{message.answer}</p>
            ) : null}
          </>
        )}

        {!isUser && message.screening_form ? (
          <div className="glp1-disclaimer">
            <ExclamationCircleOutlined style={{ marginTop: 2 }} />
            <div>
              <div className="glp1-disclaimer-title">Eligibility inputs needed</div>
              <p>Open the guided screener to run the de-identified checklist.</p>
              <Button size="small" onClick={onOpenScreener} style={{ marginTop: 8 }}>
                Open screener
              </Button>
            </div>
          </div>
        ) : null}

        {!isUser && !isPhiBlock && message.needs_screener && !message.screening_form && !message.screening ? (
          <div className="glp1-disclaimer">
            <SafetyCertificateOutlined style={{ marginTop: 2 }} />
            <div>
              <div className="glp1-disclaimer-title">Use the structured screener?</div>
              <p>This question may be easier to apply to a de-identified patient with the eligibility checklist.</p>
              <Button size="small" onClick={onOpenScreener} style={{ marginTop: 8 }}>
                Open eligibility screener
              </Button>
            </div>
          </div>
        ) : null}

        {!isUser ? <ScreeningResult screening={message.screening} /> : null}
        {!hasStructuredAnswers && !isUser ? <NextActions actions={message.next_actions} /> : null}
        {!hasStructuredAnswers && !isUser ? (
          <SourceLimitation limitations={singleSubAnswer?.answerable === false ? singleSubAnswer.limitations : []} />
        ) : null}
        {/* Show the "not a final coverage determination" notice only on the latest
            completed answer so it doesn't clutter every bubble in the transcript. */}
        {isLatest && !isUser && !isPhiBlock && message.not_final_coverage_determination ? <CoverageDisclaimer /> : null}
        {!hasStructuredAnswers && !isUser ? (
          <CitationList citations={message.citations} defaultOpen={isLatest} forceOpen={message.print_target} />
        ) : null}
        {/* Only show feedback once the answer is fully settled: text streaming done
            AND any grounding pass has completed. While the answer is still building
            the user has nothing meaningful to give feedback on yet. */}
        {!isUser && !message.streaming && message.grounding_state !== 'pending' ? (
          <FeedbackButtons message={message} onFeedback={onFeedback} />
        ) : null}
      </div>
    </div>
  );
}

export default React.memo(MessageBubble);
